import { useEffect, useMemo } from 'react';
import { paymentPageLanguage } from '@/lib/payment-page-language';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation('payments');

  // Payment result pages are customer-facing — respect lang param from edge function
  const langParam = paymentPageLanguage(searchParams.get('lang'));
  useEffect(() => {
    if (langParam && langParam !== i18n.language) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  // Transbank params
  const authCode = searchParams.get('auth');
  const amount = searchParams.get('amount');
  // P-12: a receipt that does not say who was paid cannot be shown to anyone
  // as proof. The webhook now sends it; when it is absent the line is omitted
  // rather than filled with a guess.
  const business = searchParams.get('business');
  // MercadoPago params
  const collectionId = searchParams.get('collection_id');
  const status = searchParams.get('status');

  const displayCode = authCode || collectionId;
  const codeLabel = authCode
    ? t('resultSuccess.authCode')
    : t('resultSuccess.transactionId');

  // Neither redirect source (mercadopago-webhook, transbank-webhook-return)
  // passes the business name or the order id in this URL today (checked by
  // reading both) -- only amount and a transaction/authorization code. The
  // confirmation date is real: it is when this receipt rendered, which for
  // a redirect landing right after payment is effectively the same moment.
  const confirmedAt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date()),
    [i18n.language]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-gray-900 flex flex-col items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full text-center animate-payment-card-in">
        <svg
          className="w-20 h-20 mx-auto mb-6"
          viewBox="0 0 80 80"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="40"
            cy="40"
            r="36"
            stroke="#22c55e"
            strokeWidth="4"
            strokeLinecap="round"
            className="animate-checkmark-circle"
          />
          <path
            d="M24 42l10 10 22-24"
            stroke="#22c55e"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="animate-checkmark-check"
          />
        </svg>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('resultSuccess.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-1">
          {t('resultSuccess.description')}
        </p>
        {business && (
          <p className="text-gray-900 dark:text-gray-100 font-medium mb-1">
            {t('resultSuccess.paidTo', { business })}
          </p>
        )}
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-6">
          {t('resultSuccess.confirmedAt', { date: confirmedAt })}
        </p>

        {amount && (
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{t('resultSuccess.amountPaid')}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${parseInt(amount).toLocaleString('es-CL')}
            </p>
          </div>
        )}

        {displayCode && (
          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-1">{codeLabel}</p>
            <p className="text-lg font-mono font-semibold text-blue-900 dark:text-blue-200">
              {displayCode}
            </p>
          </div>
        )}

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          {t('resultSuccess.whatsappConfirmation')}
        </p>

        <Button variant="outline" onClick={() => window.close()}>
          {t('resultSuccess.close')}
        </Button>
      </div>

      <a
        href="https://okostec.ai"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-6 flex items-center gap-2 text-gray-400 dark:text-gray-500 text-xs hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        <span>{t('poweredBy')}</span>
        <img
          src="https://storage.googleapis.com/gpt-engineer-file-uploads/YOgwp6cg3QOXTUHfe0Qjv3JHvT03/uploads/1757601334821-O.png"
          alt="Okostec"
          className="h-4 w-4"
        />
        <span className="font-medium text-gray-500 dark:text-gray-400">Okostec</span>
      </a>
    </div>
  );
}
